/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  deleteConclusion,
  saveConclusion,
} from '../../../../../../../app/page/page-storage/conclusions/conclusions.actions';
import { sessionSelectors } from '../../../../../../../app/resource/session/session.selectors';
import { userSelectors } from '../../../../../../../app/resource/user/user.selectors';
import { useSelectWithParams } from '../../../../../../../utils/selector.utils';
import { IUserRecapProps } from './types';
import { storeNotAttended } from '../../../../../../../app/page/page-storage/sessions/sessions.actions';
import { CircularAvatar } from '../../../../CustomAvatar';
import { meetingSimpleSelectors } from '../../../../../../../app/resource/meeting-simple/meeting-simple.selectors';
import { hasUserElevatedPrivilegesDuringSession } from '../../../../../../../utils/user.utils';
import { VALID_RECAP_SCORES } from '../../../../../../../utils/conclusion.utils';

export const UserRecap: FunctionComponent<IUserRecapProps> = ({
  loggedUserId,
  userId,
  sessionId,
  conclusion,
  notAttended,
  showValidationError,
  handleAttendance,
}) => {
  const session = useSelectWithParams(sessionSelectors.selectResourceById, sessionId);
  const meetingId = session?.meetingId;
  const meeting = useSelectWithParams(meetingSimpleSelectors.selectResourceById, meetingId ?? '');

  const [recapNote, setRecapNote] = useState('');
  const [currentScore, setCurrentScore] = useState<string>('');
  const notAttendedCheck = notAttended.includes(userId);

  useEffect(() => {
    setCurrentScore(conclusion?.score ?? '');
    setRecapNote(conclusion?.note ?? '');
  }, [conclusion]);

  const dispatch = useDispatch();
  const user = useSelectWithParams(userSelectors.selectResourceById, userId);

  const handleSetRecapNote = useCallback(() => {
    dispatch(
      saveConclusion(sessionId, userId, {
        note: recapNote,
      }),
    );
  }, [dispatch, sessionId, recapNote, userId]);

  const handleSetScore = useCallback(
    (score: string) => {
      dispatch(storeNotAttended(notAttended.filter((id) => id !== userId)));
      dispatch(
        saveConclusion(sessionId, userId, {
          score: score,
        }),
      );
      setCurrentScore(score);
    },
    [dispatch, sessionId, notAttended, userId],
  );

  const handleDeleteScore = useCallback(() => {
    if (currentScore !== '') {
      dispatch(deleteConclusion(sessionId, userId));
      setCurrentScore('');
    }
  }, [dispatch, sessionId, userId, currentScore]);

  const isAllowedToModify = loggedUserId === userId || meeting?.administratorIds.includes(loggedUserId);
  const hasElevatedPrivileges = hasUserElevatedPrivilegesDuringSession(loggedUserId, session, meeting);

  return (
    user && (
      <div className="single-headline recap-row col-12 p-1 pb-0">
        <div
          className={`panel flex aic px-2 ${notAttendedCheck ? 'lightDarkGray-bg' : ''} ${
            showValidationError ? 'recap-row-error' : ''
          }`}
        >
          <div className="headline-left flex aic flex20">
            <CircularAvatar
              src={user.image}
              title={`${user.firstName} ${user.lastName}`}
              classes="mr-1"
              cursorPointer={false}
            />
            {`${user.firstName} ${user.lastName}`}{' '}
          </div>
          <div style={{ cursor: 'pointer' }} className="toggle-buttons recap-buttons flex aic flex20">
            {VALID_RECAP_SCORES.map((possibleScore) => (
              <button
                key={`${user.id}/${possibleScore}`}
                disabled={isAllowedToModify === false}
                className={`recap-button ${possibleScore === currentScore ? 'selected' : ''}`}
                onClick={() => handleSetScore(possibleScore)}
              >
                {possibleScore}
              </button>
            ))}
          </div>
          {hasElevatedPrivileges && (
            <li className="flex aic" style={{ borderRight: '1px solid #E0E6EB', height: '80px', paddingRight: '20px' }}>
              <a
                className="btn btn-md red-bg ml-2"
                style={{ height: '40px' }}
                onClick={() => {
                  setRecapNote('');
                  handleDeleteScore();
                  handleAttendance(userId, notAttendedCheck ? 'remove' : 'add');
                }}
              >
                Did not attend
              </a>
            </li>
          )}
          <div className="recap-comment">
            <input
              disabled={notAttendedCheck || isAllowedToModify === false}
              type="text"
              className="recap-comment-input"
              value={recapNote}
              onChange={(e) => setRecapNote(e.target.value)}
              onBlur={handleSetRecapNote}
            />
          </div>
        </div>
      </div>
    )
  );
};
